import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Box, Grid, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import qs from "query-string";
import isArray from "lodash/isArray";

import VideoListSortingButton from "../../components/VideoListSortingButton";
import MovieAlbum from "../../components/MovieAlbum";
import MovieMenu from "../../components/MovieMenu";
import VideoTagFilteredButtons from "../../components/VideoTagFilteredButtons";

import { useTopicVideosInfinite } from "../../useGlobal";

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      width: "100%",
      margin: "6.5%",
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(5),
      overflowAnchor: "none",
      [theme.breakpoints.down("sm")]: {
        marginTop: theme.spacing(2),
      },
    },
    menu: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      marginLeft: "6.5%",
      marginRight: "6.5%",
      [theme.breakpoints.down("sm")]: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
      },
    },
    title: {
      marginBottom: theme.spacing(3),
    },
    tagList: {
      marginBottom: theme.spacing(1),
    },
    loadMoreContent: {
      marginTop: theme.spacing(5),
      [theme.breakpoints.down("xs")]: {
        marginTop: theme.spacing(0),
      },
    },
    loadMoreButton: {
      ...theme.typography.body2,
      width: 160,
      height: 40,
      marginBottom: theme.spacing(3),
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      border: "solid 1px",
      borderRadius: "0!important",
      color: theme.palette.common.grey[600],
      borderColor: theme.palette.common.grey[600],
      "&:hover": {
        color: "white",
        backgroundColor: theme.palette.common.grey[600],
      },
      [theme.breakpoints.down("xs")]: {
        width: 110,
        height: 32,
        marginBottom: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
      },
    },
    loadingOverlay: {
      position: "relative",
      minHeight: "200px",
    },
    loadingSpinner: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(2),
    },
    loadingText: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(1),
    },
  }),
);

// Simple cached component that doesn't update during loading
const CachedMovieAlbum = React.memo(
  function CachedMovieAlbum ({ slot, isLoading, ...props }) {
    // The isLoading prop is ignored in the memo comparison, so the component won't
    // re-render when only isLoading changes, but will re-render when slot changes
    return <MovieAlbum slot={slot} {...props} />;
  },
  (prevProps, nextProps) => {
    // Only re-render if the slot has changed and we're not loading
    // If we are loading, prevent re-render by returning true (props are equal)
    if (nextProps.isLoading) {
      return true; // Consider props equal during loading, prevent re-render
    }
    // Otherwise, normal comparison (re-render if slot changed)
    return prevProps.slot === nextProps.slot;
  },
);

const VideoList = props => {
  const classes = useStyles();

  // Refs for load more functionality
  const loadMoreButtonRef = useRef(null);
  const timerRef = useRef(null);
  const observerRef = useRef(null);

  // State and refs for scroll detection
  const [hasScrolledRecently, setHasScrolledRecently] = useState(false);
  const [userScrollDirection, setUserScrollDirection] = useState(null);
  const scrollTimeoutRef = useRef(null);
  const lastScrollPositionRef = useRef(0);
  const wheelEventRef = useRef(false);
  const touchEventRef = useRef(false);
  const keyEventRef = useRef(false);
  const scrollThreshold = 1; // Minimum scroll distance in pixels to consider as a real scroll

  const pathname = window.location.pathname;
  const topicId = pathname
    .replace("/video/list/", "")
    .replace("/video/list", "");

  const parsed = qs.parse(window.location.search);
  const sortKey = parsed.sort_key;
  const tagNames = !parsed.tag_name
    ? []
    : isArray(parsed.tag_name)
    ? parsed.tag_name
    : [parsed.tag_name];

  const {
    topic,
    isLoadingInitialData,
    isLoadingMore,
    isReachingEnd,
    size,
    setSize,
  } = useTopicVideosInfinite({
    topicId,
    sortKey,
    tagNames,
  });

  const tags = topic?.tags || [];

  // Click handler for load more button
  const handleClickMore = () => {
    setHasScrolledRecently(false);
    if (!isLoadingMore && !isReachingEnd) {
      setSize(size + 1);
    }
  };

  // Setup scroll detection
  useEffect(() => {
    // Track user input events that might cause scrolling
    const trackWheel = () => {
      wheelEventRef.current = true;
    };
    const trackTouch = () => {
      touchEventRef.current = true;
    };
    const trackKey = e => {
      // Space, Page Up/Down, Arrows, Home, End keys
      const scrollKeys = [32, 33, 34, 35, 36, 37, 38, 39, 40];
      if (scrollKeys.includes(e.keyCode)) {
        keyEventRef.current = true;
      }
    };

    // Track the scroll direction and determine if it's user-initiated
    const handleScroll = () => {
      const currentScrollPosition = window.scrollY;
      const scrollDistance =
        currentScrollPosition - lastScrollPositionRef.current;
      const isUserScroll =
        wheelEventRef.current || touchEventRef.current || keyEventRef.current;

      // Only consider it a real user scroll if:
      // 1. We detected user input events (wheel, touch, key)
      // 2. The scroll distance is significant
      if (isUserScroll && Math.abs(scrollDistance) >= scrollThreshold) {
        // Set scroll direction (positive = down, negative = up)
        setUserScrollDirection(scrollDistance > 0 ? "down" : "up");

        // Set scroll flag to true when user scrolls
        setHasScrolledRecently(true);

        // Clear any existing timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      }

      // Update last position
      lastScrollPositionRef.current = currentScrollPosition;

      // Reset event flags
      wheelEventRef.current = false;
      touchEventRef.current = false;
      keyEventRef.current = false;
    };

    // Store initial scroll position
    lastScrollPositionRef.current = window.scrollY;

    // Add event listeners for user input
    window.addEventListener("wheel", trackWheel, { passive: true });
    window.addEventListener("touchstart", trackTouch, { passive: true });
    window.addEventListener("keydown", trackKey);

    // Add scroll event listener with throttling for better performance
    let isScrolling = false;
    const throttledScroll = () => {
      if (!isScrolling) {
        isScrolling = true;

        // Use requestAnimationFrame for smoother performance
        window.requestAnimationFrame(() => {
          handleScroll();
          isScrolling = false;
        });
      }
    };

    window.addEventListener("scroll", throttledScroll, { passive: true });

    // Clean up
    return () => {
      window.removeEventListener("wheel", trackWheel);
      window.removeEventListener("touchstart", trackTouch);
      window.removeEventListener("keydown", trackKey);
      window.removeEventListener("scroll", throttledScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Set up the Intersection Observer for the Load More button
  useEffect(() => {
    // Skip setup if we've reached the end or initial data is still loading
    if (isReachingEnd || isLoadingInitialData) {
      return;
    }

    const options = {
      root: null, // Use the viewport
      rootMargin: "0px",
      threshold: 0.9, // 90% of the target is visible
    };

    const handleIntersection = entries => {
      const [entry] = entries;

      if (entry.isIntersecting && !isLoadingMore && !isReachingEnd) {
        // Only start timer if user has scrolled recently AND scrolled downward
        if (hasScrolledRecently && userScrollDirection === "down") {
          // Clear any existing timer when button comes into view
          if (timerRef.current) {
            clearTimeout(timerRef.current);
          }

          // Start the timer for 1000 ms
          timerRef.current = setTimeout(() => {
            handleClickMore();
          }, 1000);
        }
      } else {
        // Clear the timer when button is no longer in view
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }
    };

    // Create observer
    observerRef.current = new IntersectionObserver(handleIntersection, options);

    // Observe the load more button if it exists
    if (loadMoreButtonRef.current) {
      observerRef.current.observe(loadMoreButtonRef.current);
    }

    // Clean up
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [
    isLoadingMore,
    isReachingEnd,
    isLoadingInitialData,
    size,
    setSize,
    hasScrolledRecently,
    userScrollDirection,
  ]);

  return (
    <>
      <div>
        <Grid container>
          <Grid item xs={12}>
            <div className={classes.menu}>
              <MovieMenu />
            </div>
          </Grid>
          <div className={classes.root}>
            <Grid item xs={12} className={classes.title}>
              <Typography variant='h2'>
                {isLoadingInitialData ? (
                  <Skeleton animation='wave' width={200} />
                ) : (
                  <Box fontWeight='fontWeightBold'>{topic.topic_name}</Box>
                )}
              </Typography>
            </Grid>
            <Grid
              container
              item
              xs={12}
              className={classes.tagList}
              spacing={0}
            >
              <VideoTagFilteredButtons tags={tags} selectedNames={tagNames} />
            </Grid>
            <Grid container item xs={12} justify='flex-end' alignItems='center'>
              <Grid item>
                <VideoListSortingButton />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              className={isLoadingInitialData ? classes.loadingOverlay : ""}
            >
              {isLoadingInitialData ? (
                // Show skeletons for initial loading
                Array.from(new Array(4)).map((_, index) => (
                  <Skeleton
                    key={index}
                    animation='wave'
                    variant='rect'
                    width='100%'
                    height={200}
                    style={{ marginBottom: 16 }}
                  />
                ))
              ) : (
                <>
                  {/* The cached movie album component */}
                  <div className={classes.recommend}>
                    <CachedMovieAlbum
                      slot={topic}
                      isLoading={isLoadingMore}
                      hideMoreLink={true}
                      hoverMaskMode={"simple"}
                      extraMarginBottom={true}
                    />
                  </div>

                  {/* Visible loading indicator when fetching more */}
                  {isLoadingMore && (
                    <div className={classes.loadingSpinner}>
                      <Skeleton
                        animation='wave'
                        variant='rect'
                        width='100%'
                        height={100}
                      />
                    </div>
                  )}
                </>
              )}
            </Grid>
          </div>
        </Grid>
        <Grid
          container
          item
          xs={12}
          justify='center'
          className={classes.loadMoreContent}
        >
          <Grid item>
            {!isReachingEnd && (
              <div ref={loadMoreButtonRef}>
                <Button
                  variant='outlined'
                  className={classes.loadMoreButton}
                  onClick={handleClickMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? "載入中..." : "看更多影片"}
                </Button>
              </div>
            )}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

VideoList.defaultProps = {};

VideoList.propsTypes = {
  topicName: PropTypes.string.isRequired,
};

export default VideoList;
